import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import { registerLocaleData } from '@angular/common';

import localeEnAu from '@angular/common/locales/en-AU';
import localePt from '@angular/common/locales/pt';
import localeSl from '@angular/common/locales/sl';
import localeTr from '@angular/common/locales/tr';
import localeZhHans from '@angular/common/locales/zh-Hans';

import { Observable, BehaviorSubject } from 'rxjs';
import { hasValue } from '@shared/utils/helpers';
import { StorageService } from '@services/core/storage/storage.service';

@Injectable({ providedIn: 'root' })
export class LocalizeService {
  private readonly storeKeyRef = 'selected-locale';
  private readonly localizeApplicationKey = '::Angular:';

  public currentLocale: Observable<string>;
  private currentLocaleSubject: BehaviorSubject<string>;

  // Your loaded translations go here (keys should include the application key prefix)
  private readonly localisations: Record<string, string> = {
  };

  constructor(
    private storage: StorageService,
    @Inject(LOCALE_ID) private locale: string
  ) {
    this.currentLocaleSubject = new BehaviorSubject<string>('en-AU');
    this.currentLocale = this.currentLocaleSubject.asObservable();

    const storedLocale = this.storage.getItem<string>(this.storeKeyRef);
    if (hasValue(storedLocale)) this.currentLocaleValue = storedLocale;
  }

  /**
   * Localize with optional fallback and interpolation params.
   * - params can be an array for positional {0}, {1}...
   * - or an object for named {name}, {count}...
   */
  l(
    key: string,
    defaultValue: string | null = null,
    params?: any[] | Record<string, any>
  ): string {
    if (hasValue(key) && !key.includes(this.localizeApplicationKey)) {
      key = `${this.localizeApplicationKey}${key}`;
    }

    const base = this.localisations[key] ?? (defaultValue ?? key);
    if (!params) return base;

    // Positional: {0}, {1}, ...
    if (Array.isArray(params)) {
      return base.replace(/\{(\d+)\}/g, (_, idx) =>
        this.toText(params[Number(idx)])
      );
    }

    // Named: {name}, {count}
    if (typeof params === 'object') {
      return base.replace(/\{(\w+)\}/g, (_, name) =>
        this.toText((params as Record<string, any>)[name])
      );
    }

    return base;
  }

  private toText(v: any): string {
    if (v === null || v === undefined) return '';
    return String(v);
  }

  public set currentLocaleValue(content: string) {
    if (hasValue(content)) {
      this.currentLocaleSubject.next(content);
      this.storage.setItem(this.storeKeyRef, content);
      this.registerCurrentLocale();
    }
  }
  public get currentLocaleValue(): string {
    return this.currentLocaleSubject.getValue();
  }

  /** Register Angular locale data for pipes like date/currency, etc. */
  private registerCurrentLocale(): void {
    const locale = this.currentLocaleValue;
    if (!hasValue(locale)) return;

    switch (locale) {
      case 'en-AU': registerLocaleData(localeEnAu, locale); break;
      case 'pt-BR': registerLocaleData(localePt, locale); break;
      case 'sl': registerLocaleData(localeSl, locale); break;
      case 'tr': registerLocaleData(localeTr, locale); break;
      case 'zh-Hans': registerLocaleData(localeZhHans, locale); break;
      default: /* keep default en-US data */ break;
    }
  }
}
